import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box } from '@mui/material';
import { BarChart2, Download, X } from 'lucide-react';
import SalesReportGenerator from '../../reports/SalesReportGenerator';
import { fetchCompanies } from '../../api/company';

/**
 * Sales Report Button Component
 * Displays a button to generate sales reports when sales analysis data is available in messages
 */
const SalesReportButton = ({ messages, darkMode }) => {
  const [open, setOpen] = useState(false);
  const [salesAnalysisMessage, setSalesAnalysisMessage] = useState(null);
  const [carInsightsData, setCarInsightsData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check messages for sales analysis data
  useEffect(() => {
    const findSalesAnalysisMessage = () => {
      if (!messages || messages.length === 0) return null;
      
      // Look for the most recent server message with sales analysis data
      const salesMessage = [...messages]
        .reverse()
        .find(msg => 
          msg.sender === 'server' && 
          (
            // Either has chart data
            msg.carInsightsData ||
            // Or contains sales analysis keywords
            (msg.text && (
              msg.text.includes('vendite') || 
              msg.text.includes('venduto') || 
              msg.text.toLowerCase().includes('analisi')
            ))
          )
        );
      
      if (salesMessage) {
        //console.log("Found sales analysis message:", salesMessage);
        setSalesAnalysisMessage(salesMessage.text);
        
        // Ensure we have chart data - either from the message or synthesize it
        if (salesMessage.carInsightsData) {
          //console.log("Using existing car insights data");
          setCarInsightsData(salesMessage.carInsightsData);
        } else {
          //console.log("Generating synthetic chart data from message text");
          // Extract data from the message text to create synthetic chart data
          const salesText = salesMessage.text;
          
          // Extract key metrics using regex patterns
          const carsMatch = salesText.match(/(?:Abbiamo\s+venduto|venduto|Auto\s+vendute):\s*(\d+)\s*(?:su|di)\s*(\d+)/i);
          const remainingMatch = salesText.match(/(?:Auto\s+rimaste\s+in\s+stock|rimaste\s+in\s+stock)[^:]*:\s*(\d+)/i);
          
          // Extract brand data
          const brandMatches = [...salesText.matchAll(/([A-Za-z]+):\s*(\d+)\s*auto/gi)];
          const brands = brandMatches.map(match => ({ brand: match[1], count: parseInt(match[2]) }));
          
          // Extract model data
          const modelRegex = /([\w-]+)\s*\((\d+)\)/g;
          const modelSection = salesText.includes('Modelli top') 
            ? salesText.split('Modelli top')[1].split('\n')[0]
            : '';
          const modelMatches = [...modelSection.matchAll(modelRegex)];
          const models = modelMatches.map(match => ({ model: match[1].trim(), count: parseInt(match[2]) }));
          
          // Extract body types
          const bodyRegex = /(SUV|Berlina|berlina|City Car|city car|Wagon|wagon|coupé|Coupé)(?:\s*\((\d+)[^)]*\))?/gi;
          const bodySection = salesText.includes('Carrozzerie più richieste') 
            ? salesText.split('Carrozzerie più richieste')[1].split('\n')[0]
            : '';
          const bodyMatches = [...bodySection.matchAll(bodyRegex)];
          const bodyTypes = bodyMatches.map(match => ({ 
            type: match[1], 
            count: match[2] ? parseInt(match[2]) : 0
          }));
          
          // Create synthetic chart data
          const soldCars = carsMatch ? parseInt(carsMatch[1]) : 0;
          const totalCars = carsMatch ? parseInt(carsMatch[2]) : 0;
          const remainingCars = remainingMatch ? parseInt(remainingMatch[1]) : 0;
          
          // Extract period from the message text
          const periodMatch = salesText.match(/vendite\s+auto\s+(?:di|del)\s+([^\(]+)(?:\s*\(([^\)]+)\))?/i);
          const period = periodMatch ? periodMatch[1].trim() : 'Ultimo periodo';
          const dates = periodMatch && periodMatch[2] ? periodMatch[2].trim() : '';
          
          // Set the synthetic car insights data
          const syntheticData = {
            descriptive_information: "Analisi Vendite Auto",
            data: {
              sold: soldCars,
              total: totalCars,
              remaining: remainingCars,
              brands: brands,
              models: models,
              bodyTypes: bodyTypes
            },
            chart_type: {
              market_overview: true,
              transaction_analysis: true
            },
            chart_data: {
              distribution: {
                title: 'Distribuzione Vendite per Marca',
                labels: brands.map(b => b.brand),
                data: brands.map(b => b.count)
              },
              overall_body_styles: {
                title: 'Vendite per Tipo di Carrozzeria',
                labels: bodyTypes.map(b => b.type),
                data: bodyTypes.map(b => b.count || 1) // Ensure non-zero for display
              },
              sales_status: {
                title: 'Stato delle Vendite',
                labels: ['Vendute', 'Disponibili'],
                data: [soldCars, remainingCars]
              }
            },
            analysis_metadata: {
              total_cars_analyzed: totalCars,
              analysis_type: "transaction_analysis",
              date_range: dates ? `${period} ${dates}` : period
            }
          };
          
          setCarInsightsData(syntheticData);
        }
        
        return salesMessage;
      }
      
      return null;
    };
    
    findSalesAnalysisMessage();
  }, [messages]);
  
  // Fetch company data when dialog opens
  useEffect(() => {
    const getCompanyData = async () => {
      if (open && !companyData) {
        try {
          setLoading(true);
          const companies = await fetchCompanies();
          if (companies && companies.length > 0) {
            // Use the first company (assuming this is the user's company)
            setCompanyData(companies[0]);
          }
        } catch (error) {
          console.error('Error fetching company data:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    getCompanyData();
  }, [open, companyData]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Check if we have sales analysis data to show the button
  if (!salesAnalysisMessage) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        startIcon={<BarChart2 size={16} />}
        onClick={handleOpen}
        sx={{
          borderRadius: '20px',
          textTransform: 'none',
          ml: 1,
          borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.2)',
          color: darkMode ? 'white' : 'primary.main',
          '&:hover': {
            borderColor: darkMode ? 'white' : 'primary.main',
            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
          }
        }}
      >
        Report
      </Button>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: darkMode ? '#1e1e1e' : 'white',
            color: darkMode ? 'white' : 'black',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BarChart2 size={20} style={{ marginRight: 8 }} />
            <Typography variant="h6">Generazione Report Vendite</Typography>
          </Box>
          <Button 
            color="inherit" 
            onClick={handleClose}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <X size={20} />
          </Button>
        </DialogTitle>
        
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            Genera un report dettagliato delle vendite auto con grafici e analisi completa.
          </Typography>
          
          <SalesReportGenerator
            salesAnalysisMessage={salesAnalysisMessage}
            carInsightsData={carInsightsData}
            companyData={companyData}
            loading={loading}
            onGenerateReport={handleClose}
          />
          
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
            Il report include dati di vendita, grafici, statistiche e il logo aziendale.
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalesReportButton; 